import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Order } from 'models/order';
import { User } from 'models/user';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { environment as env } from '../../../../../src/environments/environment';
import { CartService } from '../cart/cart.service';
import { ConfigurationService } from '../configuration/configuration.service';
import { OrderService } from '../order/order.service';
import { RestApiService } from '../rest-api/rest-api.service';
import { UrlService } from '../url/url.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    public currentUser: Observable<User>;
    private currentUserSubject: BehaviorSubject<User>;
    private forgotPassObj: any;
    public language: string;

    constructor(
        private http: HttpClient,
        private _orderService: OrderService,
        private _cartService: CartService,
        private _configurationService: ConfigurationService,
        private _restApiService: RestApiService,
        private _router: Router,
        private _localizeRouterService: LocalizeRouterService,
        private _urlService: UrlService
    ) {
        if (JSON.parse(localStorage.getItem('token'))) {
            this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('token')));
        } else {
            this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(sessionStorage.getItem('token')));
        }
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    login(userName: string, password: string, remember: boolean, firstName?: any, lastName?: any) {
        const lang = this._configurationService.getSiteFromUrl();
        const _language = lang ? lang.site : 'en_uk';
        let loginObj;

        if (this._configurationService.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            //_site = 'de_de';

            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    let temp = portalId.toLocaleUpperCase().replace(/-/g, '_');
                    loginObj = {
                        userName,
                        password,
                        portalName: temp,
                        firstName,
                        lastName
                    };
                }
            });
        } else {
            loginObj = {
                userName,
                password,
                countryCode: _language,
                firstName,
                lastName
            };
        }

        return this.http.post<any>(`${env.APIEndpoint}api/v1/user`, loginObj).pipe(
            map((user) => {
                // login successful if there's a jwt token in the response
                if (user && user.token) {
                    if (remember) {
                        // store user details and jwt token in local storage to keep user logged in between page refreshes
                        localStorage.setItem('token', JSON.stringify(user.token));
                        this.currentUserSubject.next(user);
                        this.currentUser = this.currentUserSubject.asObservable();
                    } else {
                        sessionStorage.setItem('token', JSON.stringify(user.token));
                        this.currentUserSubject.next(user);
                        this.currentUser = this.currentUserSubject.asObservable();
                    }
                }

                return user;
            })
        );
    }

    loginMock(userName: string, password: string, remember: boolean) {
        return this.http.get<User>(`/assets/mock/user.json`).pipe(
            map((user) => {
                // login successful if there's a jwt token in the response
                if (user && user.token) {
                    if (remember) {
                        localStorage.setItem('token', JSON.stringify(user.token));
                        this.currentUserSubject.next(user);
                        this.currentUser = this.currentUserSubject.asObservable();
                    } else {
                        sessionStorage.setItem('token', JSON.stringify(user.token));
                        this.currentUserSubject.next(user);
                        this.currentUser = this.currentUserSubject.asObservable();
                    }
                }

                return user;
            })
        );
    }

    forgotPassword(username: string) {
        this._configurationService.currentPortal$.subscribe((portalId) => {
            let portalName;
            if (portalId) {
                portalName = portalId.toLocaleUpperCase().replace(/-/g, '_');
                let lang = JSON.parse(sessionStorage.getItem('portalLang'));
                if (lang) {
                    this.language = lang.lang.toLocaleUpperCase();
                }
            } else {
                portalName = null;
                this.language = null;
            }
            this.forgotPassObj = {
                email: username,
                portalName: portalName,
                language: this.language
            };
        });
        const _url = `${env.APIEndpoint}/api/v1/User/ForgotPassword`;

        return this.http.post<any>(_url, this.forgotPassObj).pipe(
            map((value) => {
                return value;
            })
        );
    }

    logout(): Promise<any> {
        const _url = `${env.APIEndpoint}api/v1/User/Logout`;

        return this._restApiService.requestGET<any>(_url).then(
            () => {
                // remove user from local storage to log user out
                let _site = this._configurationService.getCurrentSite();
                if (this._configurationService.isPartnerPage()) {
                    this._configurationService.currentPortal$.subscribe((portalId) => {
                        if (portalId) {
                            _site = portalId;
                        }
                    });
                }

                localStorage.removeItem(`order${_site}`);
                sessionStorage.removeItem(`order${_site}`);
                localStorage.removeItem('token');
                sessionStorage.removeItem('token');
                localStorage.removeItem(`cartItems${_site}`);
                let cartItems = {
                    courses: [],
                    events: [],
                    wishlist: []
                };
                localStorage.setItem(`cartItems${_site}`, this._urlService.encode(JSON.stringify(cartItems)));

                localStorage.removeItem('showLogin');

                this.currentUserSubject.next(null);
                const order: Order = new Order([]);
                order.participants = [];
                order.user = null;
                order.cartItems = null;
                this._orderService.updateOrder(order);

                localStorage.setItem(`itemsNumber${_site}`, '0');
                this._cartService.clearCart();
                this.redirectAfterLogout();
                return true;
            },
            (error) => {
                console.log(error);
            }
        );
    }

    isLoggedIn() {
        if (this.currentUserValue) {
            return true;
        }
        return false;
    }

    loggedIn() {
        return this.currentUserSubject.asObservable();
    }

    loggedOut() {
        return this.currentUserSubject.asObservable();
    }

    redirectAfterLogout(targetUrl?: string) {
        if (!this._configurationService.isPartnerPage()) {
            const translatedPath: any = this._localizeRouterService.translateRoute(targetUrl ? targetUrl : '/home');
            this._router.navigate([translatedPath], { skipLocationChange: true }).then(() => {});
        } else {
            this._configurationService.currentPortal$
                .pipe(
                    filter((user) => user !== null),
                    distinctUntilChanged()
                )
                .subscribe((portal) => {
                    if (portal) {
                        this._router.navigate(['/partner/' + portal]).then(() => {});
                    }
                });
        }
    }
}
